import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, RefreshCw, CheckCircle2, Zap, XCircle, Clock3, RotateCcw } from "lucide-react";
import AppShell from "../components/AppShell";
import HealthGauge from "../components/HealthGauge";
import ActionCard from "../components/ActionCard";
import MergeCard from "../components/MergeCard";
import TagCenterCard from "../components/TagCenterCard";
import ConsentDndCard from "../components/customer/ConsentDndCard";
import { scanIdentity } from "../lib/scanIdentity";
import {
  ActionButton,
  CustomerPanel,
  EmptyState,
  MetricCard,
  ScanOutcomeSummary,
  StatusBadge,
} from "../components/customer/CustomerUI";
import api, { formatApiError } from "../lib/api";

function finiteNumber(value) {
  if (value === null || value === undefined || (typeof value === "string" && value.trim() === "")) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizedStage(value) {
  return String(value || "running").replaceAll("_", " ");
}

function displayCount(value) {
  return (finiteNumber(value) || 0).toLocaleString();
}

function progressValue(value) {
  const progress = finiteNumber(value) || 0;
  return Math.min(100, Math.max(0, progress));
}

function estimatedTime(value) {
  const seconds = finiteNumber(value);
  if (seconds === null) return "Calculating…";
  return seconds < 60 ? `${seconds}s` : `${Math.ceil(seconds / 60)} min`;
}

export function RunningScanPanel({ scan, cancelling, onCancel }) {
  const queued = scan.status === "queued";
  const progress = progressValue(scan.progress);
  const queuePosition = finiteNumber(scan.queue_position);
  const queueDepth = finiteNumber(scan.queue_depth);
  const queueValue = queuePosition === null
    ? "Not assigned"
    : queueDepth === null
      ? String(queuePosition)
      : `${queuePosition} of ${queueDepth}`;
  const stage = normalizedStage(scan.current_stage || scan.status);

  return (
    <CustomerPanel
      title="Scanning your CRM"
      description={`${scan.connector_name || "Your CRM"} is connected in read-only mode. No records are modified during the scan.`}
    >
      <div className="p-ui-4 sm:p-ui-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <StatusBadge tone={queued ? "warning" : "info"}>{stage}</StatusBadge>
          <ActionButton onClick={onCancel} disabled={cancelling || scan.cancel_requested}
                        variant="destructive" size="compact"
                        data-testid="cancel-scan-button">
            <XCircle className="h-3.5 w-3.5" />
            {scan.cancel_requested ? "Stopping safely…" : cancelling ? "Requesting…" : "Cancel scan"}
          </ActionButton>
        </div>

        {queued && (
          <p className="mt-ui-3 text-sm text-slate-600">
            Waiting for an available scan worker{queuePosition === null ? "." : ` · Queue position ${queueValue}.`}
          </p>
        )}

        <div className="mt-ui-4 grid grid-cols-2 gap-ui-3 md:grid-cols-4">
          <MetricCard label="Contacts staged" value={displayCount(scan.total_contacts)} />
          <MetricCard label={queued ? "Queue position" : "Pages saved"}
                      value={queued ? queueValue : displayCount(scan.pages_processed)} />
          <MetricCard label="Estimated remaining" value={estimatedTime(scan.eta_seconds)} icon={Clock3} />
          <MetricCard label="Progress" value={`${progress}%`} />
        </div>

        {scan.status_message && <p className="mt-ui-3 text-xs text-slate-500">{scan.status_message}</p>}
        <div className="mt-ui-4">
          <div
            role="progressbar"
            aria-label="Scan progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            className="h-1.5 w-full overflow-hidden bg-slate-200"
          >
            <div className="h-full bg-brand transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>
    </CustomerPanel>
  );
}

export default function ScanReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [autoFixStats, setAutoFixStats] = useState(null);
  const [changesApplied, setChangesApplied] = useState(false);
  const [changeRequests, setChangeRequests] = useState([]);
  const [outcomeSummary, setOutcomeSummary] = useState(undefined);
  const [revertingId, setRevertingId] = useState(null);
  const [rescanning, setRescanning] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const timer = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(`/scans/${id}`);
      setScan(data);
      if (["completed", "failed", "cancelled"].includes(data.status)) {
        clearInterval(timer.current);
      }
    } catch (e) { clearInterval(timer.current); }
  }, [id]);

  useEffect(() => {
    poll();
    timer.current = setInterval(poll, 1200);
    return () => clearInterval(timer.current);
  }, [poll]);

  const loadChangeRequests = useCallback(async () => {
    try {
      const { data } = await api.get(`/change-requests?scan_id=${id}`);
      setChangeRequests(data);
    } catch {
      setChangeRequests([]);
    }
  }, [id]);

  useEffect(() => {
    if (scan?.connection_id && (scan.status === "completed")) {
      api.get(`/connections/${scan.connection_id}/auto-fix-stats`)
        .then(({ data }) => setAutoFixStats(data)).catch(() => {});
      loadChangeRequests();
      api.get(`/scans/${id}/outcome-summary`)
        .then(({ data }) => setOutcomeSummary(data))
        .catch(() => setOutcomeSummary(null));
    }
  }, [id, scan?.connection_id, scan?.status, loadChangeRequests]);

  const running = scan && (scan.status === "queued" || scan.status === "running");
  const visibleFindings = (scan?.findings || []).filter(
    (finding) => finding.key !== "missing_business_info"
      && String(finding.category || "").toLowerCase() !== "enrichment"
  );
  const critical = visibleFindings.filter((f) => f.severity === "critical").length;
  const warnings = visibleFindings.filter((f) => f.severity === "warning").length;
  const actionCardCount = visibleFindings.length + (scan?.consent_summary ? 1 : 0);
  const bulkDeleteCapability = scan?.action_capabilities?.bulk_delete || {
    available: false,
    reason: null,
  };
  const consentDndDeleteCapability = scan?.action_capabilities?.consent_dnd_delete || {
    available: false,
    reason: "Deletion availability is unavailable for this scan.",
  };

  const cancelScan = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post(`/scans/${id}/cancel`);
      setScan(data);
      toast.success(data.status === "cancelled" ? "Scan cancelled." : "Cancellation requested.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCancelling(false);
    }
  };

  const rescan = async () => {
    setRescanning(true);
    try {
      const { data } = await api.post(`/connections/${scan.connection_id}/scan`);
      navigate(`/scans/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRescanning(false); }
  };

  const markChangesApplied = () => {
    setChangesApplied(true);
    loadChangeRequests();
  };

  const revertChange = async (change) => {
    if (!window.confirm(`Restore ${change.contact_name || "this contact"}'s ${change.field} to its exact value before this approved fix?`)) return;
    setRevertingId(change.id);
    try {
      await api.post(`/change-requests/${change.id}/revert`);
      toast.success("Change reverted and verified against the CRM.");
      setChangesApplied(false);
      await loadChangeRequests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <AppShell>
      <ActionButton onClick={() => navigate("/dashboard")} data-testid="scan-back-button"
                    variant="quiet" size="compact" className="mb-ui-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
      </ActionButton>

      {!scan ? (
        <CustomerPanel>
          <EmptyState title="Loading scan…" description="CleanIQ is retrieving the latest scan state." />
        </CustomerPanel>
      ) : running ? (
        <RunningScanPanel scan={scan} cancelling={cancelling} onCancel={cancelScan} />
      ) : scan.status === "failed" || scan.status === "cancelled" ? (
        <CustomerPanel data-testid={scan.status === "cancelled" ? "scan-cancelled" : "scan-failed"} className="border-health-critical/30 bg-red-50">
          <EmptyState
            icon={scan.status === "cancelled" ? XCircle : AlertTriangle}
            title={scan.status === "cancelled" ? "Scan cancelled safely" : "Scan failed"}
            description={scan.status_message || scan.error}
            action={<ActionButton onClick={() => navigate("/dashboard")} variant="secondary">Back to dashboard</ActionButton>}
          />
        </CustomerPanel>
      ) : (
        <div>
          {(scan.auto_fixed > 0 || autoFixStats?.auto_fix_enabled) && (
            <CustomerPanel data-testid="auto-fix-banner" className="mb-ui-6 border-brand/40 bg-brand/5">
              <div className="flex flex-wrap items-center justify-between gap-ui-3 px-ui-4 py-ui-3">
              <span className="inline-flex items-center gap-2 text-sm text-slate-800">
                <Zap className="h-4 w-4 text-brand" />
                {scan.auto_fixed > 0
                  ? <>Auto-Fix applied <strong>{scan.auto_fixed}</strong> safe fix{scan.auto_fixed === 1 ? "" : "es"} automatically after this scan — the Health Score below reflects the cleaned data.</>
                  : <>Auto-Fix is <strong>ON</strong> for this connection. Safe fixes are applied automatically after each scan.</>}
              </span>
              {autoFixStats?.lifetime_applied > 0 && (
                <StatusBadge data-testid="auto-fix-lifetime" tone="info">
                  {autoFixStats.lifetime_applied} applied lifetime
                </StatusBadge>
              )}
              </div>
            </CustomerPanel>
          )}
          {changesApplied && (
            <CustomerPanel data-testid="rescan-banner" className="mb-ui-6 border-health-good/40 bg-emerald-50">
              <div className="flex flex-wrap items-center justify-between gap-ui-3 px-ui-4 py-ui-3">
              <span className="inline-flex items-center gap-2 text-sm text-emerald-800">
                <CheckCircle2 className="h-4 w-4" /> Approved changes were written back to your CRM. Run a fresh scan to see your improved Health Score.
              </span>
              <ActionButton data-testid="rescan-button" onClick={rescan} disabled={rescanning}>
                {rescanning ? "Starting…" : "Run fresh scan"}
              </ActionButton>
              </div>
            </CustomerPanel>
          )}
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0 w-full">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">CRM Health Report</div>
              <h1 className="mt-1 break-words font-heading text-3xl font-bold tracking-tight text-slate-900">{scanIdentity(scan).name}</h1>
              <p className="mt-2 text-sm text-slate-600">{scanIdentity(scan).provider} · {scanIdentity(scan).dateLabel}</p>
              <p className="mt-3 text-sm text-slate-700">Review what CleanIQ found and choose your next action. One contact can contribute multiple issue occurrences. Actions require your approval.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="space-y-4 lg:col-span-8">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  Scan action cards — {actionCardCount} action cards
                </h2>
                <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-slate-400">
                  <RefreshCw className="h-3 w-3" /> Scan evidence is read-only · actions require approval
                </span>
              </div>
              <ConsentDndCard
                summary={scan.consent_summary}
                legacy={!scan.consent_summary}
                connectionId={scan.connection_id}
                scanId={scan.id}
                index={0}
                bulkDeleteCapability={consentDndDeleteCapability}
                onApplied={markChangesApplied}
              />
              {visibleFindings.length === 0 ? (
                <CustomerPanel className="border-health-good/30 bg-emerald-50">
                  <EmptyState
                    icon={CheckCircle2}
                    title="No data-quality issues detected"
                    description="Your CRM looks clean."
                  />
                </CustomerPanel>
              ) : (
                visibleFindings.map((f, i) =>
                  f.key === "duplicates" && f.clusters?.length > 0 ? (
                    <MergeCard key={f.key} finding={f} index={i}
                               connectionId={scan.connection_id} scanId={scan.id}
                               onApplied={markChangesApplied} />
                  ) : f.key === "tag_audit" ? (
                    <TagCenterCard key={f.key} finding={f} index={i}
                                   connectionId={scan.connection_id} scanId={scan.id}
                                   onApplied={markChangesApplied} />
                  ) : (
                    <ActionCard key={f.key} finding={f} index={i}
                                connectionId={scan.connection_id} scanId={scan.id}
                                bulkDeleteCapability={bulkDeleteCapability}
                                onApplied={markChangesApplied} />
                  )
                )
              )}
            </div>
            <div className="lg:col-span-4">
              <HealthGauge score={scan.health_score} breakdown={scan.score_breakdown} />
              <div className="mt-ui-4 grid grid-cols-3 gap-ui-3">
                {[
                  { label: "Contacts", value: scan.total_contacts, cls: "text-slate-900" },
                  { label: "Critical", value: critical, cls: "text-health-critical" },
                  { label: "Warnings", value: warnings, cls: "text-health-warn" },
                ].map((s) => (
                  <MetricCard key={s.label} label={s.label} value={s.value} className="text-center" valueClassName={s.cls} />
                ))}
              </div>
            </div>

          </div>
          <ScanOutcomeSummary
            summary={outcomeSummary}
            loading={outcomeSummary === undefined}
          />

          {changeRequests.length > 0 && (
            <CustomerPanel data-testid="change-history" className="mb-ui-6">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                <div>
                  <h2 className="font-semibold text-slate-900">Approved change history</h2>
                  <p className="mt-0.5 text-xs text-slate-500">Every write-back is traceable. Applied field changes can be restored to the exact pre-fix value.</p>
                </div>
                <ActionButton data-testid="change-history-rescan" onClick={rescan} disabled={rescanning}
                              variant="secondary" size="compact">
                  {rescanning ? "Starting…" : "Run fresh scan"}
                </ActionButton>
              </div>
              <div className="divide-y divide-border">
                {changeRequests.map((change) => (
                  <div key={change.id} data-testid={`change-history-${change.id}`}
                       className="grid gap-3 px-4 py-3 text-sm md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-center">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-slate-900">{change.contact_name || change.contact_id}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">{change.field}</div>
                    </div>
                    <div className="min-w-0 text-xs text-slate-600">
                      <span className="break-all">{String(change.previous_value ?? "") || "blank"}</span>
                      <span className="px-2 text-slate-400">→</span>
                      <span className="break-all font-medium text-slate-800">{String(change.new_value ?? "") || "blank"}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3 md:justify-end">
                      <StatusBadge tone={change.status === "applied" ? "success" : change.status.includes("failed") ? "danger" : "neutral"}>
                        {change.status.replaceAll("_", " ")}
                      </StatusBadge>
                      {change.status === "applied" && (
                        <ActionButton data-testid={`revert-change-${change.id}`} onClick={() => revertChange(change)}
                                      disabled={revertingId === change.id}
                                      variant="secondary" size="compact"
                                      className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100">
                          <RotateCcw className="h-3.5 w-3.5" />
                          {revertingId === change.id ? "Restoring…" : "Revert"}
                        </ActionButton>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CustomerPanel>
          )}

        </div>
      )}
    </AppShell>
  );
}
